import type { PulseContentItem } from '../types';

interface PulseOverlayProps {
  item: PulseContentItem;
}

/**
 * Renders the appropriate state overlay(s) for a pulse cell.
 *
 * Priority (highest wins for full-cell overlays):
 * 1. Deleted — large X, desaturated
 * 2. Moderated — red tint, fading deeper
 * 3. Trending — fire border (handled by PulseCell class)
 * 4. New — pulsing blue badge
 * 5. Monetized — $ badge (always shown if applicable, non-exclusive)
 * 6. Active — subtle green dot
 */
export function PulseOverlay({ item }: PulseOverlayProps) {
  return (
    <>
      {/* DELETED: full-cell X overlay */}
      {item.state === 'deleted' && (
        <div className="pulse-overlay-deleted absolute inset-0 flex items-center justify-center">
          <svg
            viewBox="0 0 100 100"
            className="size-3/5 pulse-anim-draw-x"
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
          >
            <line x1="15" y1="15" x2="85" y2="85" className="text-red-500" />
            <line x1="85" y1="15" x2="15" y2="85" className="text-red-500" />
          </svg>
        </div>
      )}

      {/* MODERATED: red tint overlay that deepens */}
      {item.state === 'moderated' && (
        <div className="pulse-overlay-moderated absolute inset-0 bg-red-900/40 pulse-anim-moderate" />
      )}

      {/* TRENDING: fire icon top-right */}
      {item.state === 'trending' && (
        <div className="absolute right-1 top-1 pulse-anim-bounce-in">
          <span className="flex items-center gap-0.5 rounded bg-orange-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">
            <svg className="size-3" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1c.6 2.2.2 3.8-.5 5 .8-.4 1.8-1.6 2-2.2C11 5.6 12 8 12 10a4 4 0 1 1-8 0c0-1.5.6-3 1.5-4.2-.1 1.2.4 2.4 1.2 3.2C6.2 6.6 6 4 8 1z"/>
            </svg>
            HOT
          </span>
        </div>
      )}

      {/* NEW: pulsing blue badge top-left */}
      {item.state === 'new' && (
        <div className="absolute left-1 top-1 pulse-anim-new-badge">
          <span className="rounded bg-[#0194f5]/90 px-1.5 py-0.5 text-[10px] font-bold text-white shadow-[0_0_8px_rgba(1,148,245,0.6)]">
            NEW
          </span>
        </div>
      )}

      {/* MONETIZED: dollar badge bottom-right (non-exclusive, shown alongside other states) */}
      {item.monetized && item.state !== 'deleted' && item.state !== 'moderated' && (
        <div className="absolute bottom-1 right-1 pulse-anim-bounce-in">
          <span className="flex size-5 items-center justify-center rounded-full bg-emerald-500/90 text-[11px] font-bold text-white shadow-md">
            $
          </span>
        </div>
      )}

      {/* ACTIVE: subtle green dot bottom-right (only when no other prominent indicator) */}
      {item.state === 'active' && !item.monetized && (
        <div className="absolute bottom-1.5 right-1.5">
          <span className="block size-2 rounded-full bg-emerald-400 shadow-[0_0_4px_rgba(52,211,153,0.7)]" />
        </div>
      )}

      {/* PROCESSING: spinner overlay */}
      {item.state === 'processing' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
          <div className="size-8 animate-spin rounded-full border-2 border-white/30 border-t-white" />
        </div>
      )}

      {/* View count for trending/high-engagement items */}
      {item.state === 'trending' && item.viewsRecent > 0 && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1 pt-4">
          <span className="text-[10px] tabular-nums text-white/80">
            {formatCount(item.viewsRecent)} views
          </span>
        </div>
      )}
    </>
  );
}

/** Format a number with K/M suffixes for compact display. */
function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
